import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { NewsCard } from '../components/NewsCard'
import { ThemeToggle } from '../components/ThemeToggle'
import { Loader2, AlertCircle, Home, Search, X, Calendar, Filter, XCircle, Newspaper } from 'lucide-react'
import { format } from 'date-fns'
import { Calendar as CalendarComponent } from '../components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover'
import { Button } from '../components/ui/button'

interface NewsArticle {
  id: string
  title: string
  summary: string
  imageUrl: string
  source: string
  url: string
  publishedAt: string
  hasVideo?: boolean
  videoUrl?: string
}

interface NewsApiResponse {
  success: boolean
  data: NewsArticle[]
  total: number
  page: number
  limit: number
  totalPages: number
  message?: string
}

export function NewsPage() {
  const [articles, setArticles] = useState<NewsArticle[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(0)
  const [totalArticles, setTotalArticles] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [fromDate, setFromDate] = useState<Date | undefined>()
  const [toDate, setToDate] = useState<Date | undefined>()
  const [showFromCalendar, setShowFromCalendar] = useState(false)
  const [showToCalendar, setShowToCalendar] = useState(false)

  // Debounce search input (300ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery)
      setCurrentPage(1) // Reset to page 1 when search changes
    }, 300)

    return () => clearTimeout(timer)
  }, [searchQuery])

  const fetchArticles = async (page: number = 1, search: string = '', from?: Date, to?: Date) => {
    try {
      setLoading(true)
      setError(null)

      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000'
      let url = `${apiUrl}/api/news/all?page=${page}&limit=12`

      if (search) {
        url += `&search=${encodeURIComponent(search)}`
      }

      if (from) {
        url += `&from=${format(from, 'yyyy-MM-dd')}`
      }

      if (to) {
        url += `&to=${format(to, 'yyyy-MM-dd')}`
      }

      const response = await fetch(url)

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data: NewsApiResponse = await response.json()

      if (data.success) {
        setArticles(data.data)
        setTotalPages(data.totalPages)
        setTotalArticles(data.total)
        setCurrentPage(data.page)

        // Show message if no articles found
        if (data.data.length === 0 && data.message) {
          setError(data.message)
        }
      } else {
        setError(data.message || 'Failed to fetch articles')
      }
    } catch (err) {
      console.error('[NewsPage] Failed to fetch articles:', err)
      setError('Failed to load articles. Please try again later.')
      setArticles([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchArticles(1, debouncedSearch, fromDate, toDate)
  }, [debouncedSearch])

  // Reset to page 1 when date range changes
  useEffect(() => {
    setCurrentPage(1)
    fetchArticles(1, debouncedSearch, fromDate, toDate)
  }, [fromDate, toDate])

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      window.scrollTo({ top: 0, behavior: 'smooth' })
      fetchArticles(newPage, debouncedSearch, fromDate, toDate)
    }
  }

  const handleClearSearch = () => {
    setSearchQuery('')
  }

  const handleClearDateFilters = () => {
    setFromDate(undefined)
    setToDate(undefined)
  }

  const handleFromDateSelect = (date: Date | undefined) => {
    if (date) {
      setFromDate(date)
      setShowFromCalendar(false)
      // Validate that from is before to
      if (toDate && date > toDate) {
        setToDate(undefined)
      }
    }
  }

  const handleToDateSelect = (date: Date | undefined) => {
    if (date) {
      setToDate(date)
      setShowToCalendar(false)
      // Validate that to is after from
      if (fromDate && date < fromDate) {
        setFromDate(undefined)
      }
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-muted to-background dark:from-background dark:to-muted py-12 px-4 sm:px-6 lg:px-8">
      <div className="fixed top-4 right-4 z-50">
        <ThemeToggle />
      </div>

      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Header */}
          <div className="mb-8">
            <Link
              to="/"
              className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 mb-6 transition-colors duration-200"
            >
              <Home className="w-5 h-5 mr-2" />
              Back to Home
            </Link>
            <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-400 dark:to-purple-400 bg-clip-text text-transparent">
              AI News & Insights
            </h1>
            <p className="mt-4 text-muted-foreground text-lg">
              Explore the latest developments in artificial intelligence
              {!loading && totalArticles > 0 && (
                <span className="ml-2 px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-sm font-medium">
                  {totalArticles} articles{searchQuery && ` matching "${searchQuery}"`}
                </span>
              )}
            </p>

            {/* Search Bar */}
            <div className="mt-6 relative">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search articles by title..."
                  className="w-full pl-12 pr-24 py-4 bg-card border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-all"
                />
                {searchQuery && (
                  <motion.button
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    onClick={handleClearSearch}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 p-2 hover:bg-muted rounded-lg transition-colors"
                    aria-label="Clear search"
                  >
                    <X className="w-5 h-5 text-muted-foreground hover:text-foreground" />
                  </motion.button>
                )}
              </div>
            </div>

            {/* Date Range Filters */}
            <div className="mt-6 flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Filter className="w-5 h-5 text-muted-foreground" />
                <span className="text-sm font-medium text-foreground">Date Range:</span>
              </div>

              {/* From Date */}
              <Popover open={showFromCalendar} onOpenChange={setShowFromCalendar}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={`
                      w-[200px] justify-start text-left font-normal
                      ${!fromDate ? 'text-muted-foreground' : 'text-foreground'}
                    `}
                  >
                    <Calendar className="mr-2 h-4 w-4" />
                    {fromDate ? format(fromDate, 'PPP') : 'From date...'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <CalendarComponent
                    mode="single"
                    selected={fromDate}
                    onSelect={handleFromDateSelect}
                    initialFocus
                    disabled={(date) => date > new Date() || (toDate ? date > toDate : false)}
                  />
                </PopoverContent>
              </Popover>

              {/* To Date */}
              <Popover open={showToCalendar} onOpenChange={setShowToCalendar}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={`
                      w-[200px] justify-start text-left font-normal
                      ${!toDate ? 'text-muted-foreground' : 'text-foreground'}
                    `}
                  >
                    <Calendar className="mr-2 h-4 w-4" />
                    {toDate ? format(toDate, 'PPP') : 'To date...'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <CalendarComponent
                    mode="single"
                    selected={toDate}
                    onSelect={handleToDateSelect}
                    initialFocus
                    disabled={(date) => date > new Date() || (fromDate ? date < fromDate : false)}
                  />
                </PopoverContent>
              </Popover>

              {/* Clear Filters Button */}
              {(fromDate || toDate) && (
                <motion.button
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  onClick={handleClearDateFilters}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-card border border-border rounded-lg hover:bg-muted transition-colors text-sm font-medium text-foreground"
                  aria-label="Clear date filters"
                >
                  <XCircle className="w-4 h-4" />
                  Clear Filters
                </motion.button>
              )}
            </div>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="flex flex-col items-center justify-center py-20">
              <Loader2 className="w-12 h-12 text-blue-600 dark:text-blue-400 animate-spin" />
              <p className="mt-4 text-muted-foreground">Loading articles...</p>
            </div>
          )}

          {/* Error State */}
          {!loading && error && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-card rounded-2xl shadow-xl p-8 mb-8 border-l-4 border-red-500"
            >
              <div className="flex items-start gap-4">
                <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    {error.includes('No articles') ? 'No Articles Found' : 'Error Loading Articles'}
                  </h3>
                  <p className="text-muted-foreground mb-4">{error}</p>
                  {error.includes('No articles') ? (
                    <Link
                      to="/"
                      className="inline-flex items-center px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors"
                    >
                      Return to Homepage
                    </Link>
                  ) : (
                    <button
                      onClick={() => fetchArticles(currentPage, debouncedSearch, fromDate, toDate)}
                      className="inline-flex items-center px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors"
                    >
                      Try Again
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {/* Empty State - No articles match filters */}
          {!loading && !error && articles.length === 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-card rounded-2xl shadow-xl p-12 mb-8 text-center"
            >
              <div className="flex flex-col items-center gap-6">
                <div className="relative">
                  <div className="absolute inset-0 bg-blue-100 dark:bg-blue-900/20 rounded-full blur-3xl opacity-50"></div>
                  <Newspaper className="relative w-24 h-24 text-muted-foreground/50 mx-auto" />
                </div>

                <div className="space-y-3">
                  <h3 className="text-2xl font-bold text-foreground">No Articles Found</h3>
                  <p className="text-muted-foreground max-w-md mx-auto">
                    {searchQuery && (fromDate || toDate)
                      ? `No articles matching "${searchQuery}" were found in the selected date range. Try adjusting your filters or search term.`
                      : searchQuery
                      ? `No articles matching "${searchQuery}" were found. Try a different search term.`
                      : fromDate || toDate
                      ? 'No articles were found in the selected date range. Try a different date range.'
                      : 'No articles are available at the moment. Please check back later.'}
                  </p>
                </div>

                <div className="flex flex-wrap items-center justify-center gap-4">
                  {/* Clear Search Button */}
                  {searchQuery && (
                    <button
                      onClick={handleClearSearch}
                      className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 dark:bg-blue-500 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors font-medium"
                    >
                      <XCircle className="w-5 h-5" />
                      Clear Search
                    </button>
                  )}

                  {/* Clear Date Filters Button */}
                  {(fromDate || toDate) && (
                    <button
                      onClick={handleClearDateFilters}
                      className="inline-flex items-center gap-2 px-6 py-3 bg-card border border-border hover:bg-muted text-foreground rounded-lg transition-colors font-medium"
                    >
                      <XCircle className="w-5 h-5" />
                      Clear Date Filters
                    </button>
                  )}

                  {/* Back to Home Button */}
                  <Link
                    to="/"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-card border border-border hover:bg-muted text-foreground rounded-lg transition-colors font-medium"
                  >
                    <Home className="w-5 h-5" />
                    Back to Home
                  </Link>
                </div>
              </div>
            </motion.div>
          )}

          {/* Articles Grid */}
          {!loading && articles.length > 0 && (
            <>
              <motion.div
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                {articles.map((article, index) => (
                  <NewsCard key={article.id} article={article} index={index} />
                ))}
              </motion.div>

              {/* Pagination */}
              {totalPages > 1 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                  className="flex items-center justify-center gap-4 mt-12"
                >
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-6 py-3 bg-card border border-border rounded-lg hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-all font-medium text-foreground"
                  >
                    ← Previous
                  </button>

                  <div className="flex items-center gap-2">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum: number
                      if (totalPages <= 5) {
                        pageNum = i + 1
                      } else if (currentPage <= 3) {
                        pageNum = i + 1
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i
                      } else {
                        pageNum = currentPage - 2 + i
                      }

                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`w-10 h-10 rounded-lg font-medium transition-all ${
                            currentPage === pageNum
                              ? 'bg-blue-600 dark:bg-blue-500 text-white'
                              : 'bg-card border border-border hover:bg-muted text-foreground'
                          }`}
                        >
                          {pageNum}
                        </button>
                      )
                    })}
                  </div>

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-6 py-3 bg-card border border-border rounded-lg hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-all font-medium text-foreground"
                  >
                    Next →
                  </button>
                </motion.div>
              )}

              {/* Page Info */}
              <div className="text-center mt-6 text-muted-foreground text-sm">
                Page {currentPage} of {totalPages} • {totalArticles} total articles
              </div>
            </>
          )}
        </motion.div>
      </div>
    </div>
  )
}
